import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import { DMS_CUSTOM_QUERY_HISTORY_STATUSES } from "models";
import "modules/DMSv3/CustomQueries/components/QueryHistoryTable/QueryHistoryTable.scss";
import { getStatusName } from "modules/DMSv3/CustomQueries/utils";
import { PAGE_SIZE } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { DMSCustomQueryHistory, DMSCustomQueryHistoryID } from "models";

type QueryHistoryTableProps = {
  page: number;
  isLoading: boolean;
  selectedQueryId?: DMSCustomQueryHistoryID;
  items?: DMSCustomQueryHistory[];
  nbPages?: number;
  nbItems?: number;
  onSelectRow: (query: DMSCustomQueryHistory) => void;
  onPageChange: (page: number) => void;
};

const columnHelper = createColumnHelper<DMSCustomQueryHistory>();

const scrollContainerToTop = () => {
  const container = document.getElementById("containerId");
  container?.scrollTo({ top: 0 });
};

export const QueryHistoryTable = ({ items = [], page, nbPages = 0, nbItems, isLoading, selectedQueryId, onSelectRow, onPageChange }: QueryHistoryTableProps) => {
  const { t } = useTranslation();

  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    const newState = typeof updater === "function" ? updater({ pageIndex: page - 1, pageSize: PAGE_SIZE.PageSize100 }) : updater;
    if (newState.pageIndex + 1 === page) return;

    onPageChange(newState.pageIndex + 1);
    scrollContainerToTop();
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "id",
        size: 60,
        header: () => <span>ID</span>,
        cell: ({ row: { original } }) => <span className={selectedQueryId === original.id ? "row-selected" : ""}>{original.id}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "name",
        size: 120,
        header: () => <span>{t("v8_query_name", "Query name")}</span>,
        cell: ({ row: { original } }) => <span className="text-ellipsis">{original.name}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "statement",
        size: 200,
        header: () => <span>{t("v8_query", "Query")}</span>,
        cell: ({ row: { original } }) => <span className="text-ellipsis">{original.statement}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "status",
        size: 90,
        header: () => <span>{t("v8_status", "Status")}</span>,
        cell: ({ row: { original } }) => <span>{getStatusName(t, original.status)}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "executed_at",
        size: 130,
        header: () => <span>{t("v8_executed_at", "Executed at")}</span>,
        cell: ({ row: { original } }) => (
          <span>{original.status !== DMS_CUSTOM_QUERY_HISTORY_STATUSES.Pending ? formatDate(original.executed_at, DATE_FORMATS_FNS.yearMonthDatehoursMinutes) : ""}</span>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "execution_duration",
        size: 90,
        header: () => <span>{t("v8_duration", "Duration")}</span>,
        cell: ({ row: { original } }) => <span>{original.execution_duration}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "author",
        size: 120,
        header: () => <span>{t("v8_author", "Author")}</span>,
        cell: ({ row: { original } }) => <span>{`${original.user?.first_name ?? ""} ${original.user?.last_name ?? ""}`.trim()}</span>
      })
    ],
    [t, selectedQueryId]
  );

  const pagination = nbPages > 1 ? { pageIndex: page - 1, pageSize: PAGE_SIZE.PageSize100 } : undefined;

  return (
    <div className="QueryHistoryTable">
      <ReactTable
        columns={columns}
        data={items}
        isFetching={isLoading}
        pageSize={PAGE_SIZE.PageSize100}
        pageCount={nbPages}
        totalItems={nbItems}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        onRowClick={onSelectRow}
      />
    </div>
  );
};
